import { createSelector } from "@reduxjs/toolkit";
import { ApiRenderComponents } from "@nxplatform/helpers/entities";
import { AuthProvidersList, TaxStrategyTypes } from "@nxplatform/platform-entities";
import { PeriodType } from "@nxplatform/platform-entities/customer/limits";
import { getPlatformSettingsState } from "@nxplatform/store-shared/platform-settings/shared-platform-settings-selectors";
var LimitNames = /* @__PURE__ */ ((LimitNames2) => {
  LimitNames2["wagerPerPeriod"] = "wagerPerPeriod";
  LimitNames2["singleBet"] = "singleBet";
  return LimitNames2;
})(LimitNames || {});
const selectPlatformSettingsIsFrontRenderText = createSelector(
  getPlatformSettingsState,
  (s) => s.isFrontRenderTextPage
);
const selectBetSharingEnabled = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a;
    return (_a = s.sport) == null ? void 0 : _a.betSharingEnabled;
  }
);
const selectLiveEventsDisabled = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a;
    return !!((_a = s == null ? void 0 : s.sport) == null ? void 0 : _a.hideLiveEvents);
  }
);
const selectPlatformSettingsLimitsDisablable = createSelector(
  getPlatformSettingsState,
  (s) => s.limitsDisablable
);
const selectLeaguesWithSportIcons = createSelector(
  getPlatformSettingsState,
  (s) => s.leaguesWithSportIcon
);
const selectWsIsActive = createSelector(
  getPlatformSettingsState,
  (s) => {
    return s.useSockets;
  }
);
const selectGrpcIsActiveByConfig = createSelector(
  getPlatformSettingsState,
  (s) => s.isGrpcEnabled
);
const selectIsTermsAcceptRequired = createSelector(
  getPlatformSettingsState,
  (s) => s.termsAcceptRequired
);
const showLeagueOnlySportIcon = createSelector(
  selectLeaguesWithSportIcons,
  (leaguesOnlySport, id) => Number(id),
  (leaguesOnlySport, id) => !!leaguesOnlySport.find((leagueId) => Number(leagueId) === id)
);
const selectPlatformSettingsCasinoEnabled = createSelector(
  getPlatformSettingsState,
  (s) => !!s.casino.enabled
);
const selectPlatformSettingsLiveCasinoEnabled = createSelector(
  getPlatformSettingsState,
  (s) => !!s.casino.liveEnabled
);
const selectPlatformSettingsCasinoShowTopWinners = createSelector(
  getPlatformSettingsState,
  (s) => s.casino.showTopWinners
);
const selectPlatformSettingsDisableSportsBetting = createSelector(
  getPlatformSettingsState,
  (s) => s.disableSportsBetting
);
const selectPlatformSettingsSmarticoActions = createSelector(
  getPlatformSettingsState,
  (s) => s.smarticoActions
);
const selectPlatformSettingsCasinoPlayersOnline = createSelector(
  getPlatformSettingsState,
  (s) => s.casino.playersOnline || {}
);
const selectPlatformSettingsRtp = createSelector(
  getPlatformSettingsState,
  (s) => s.casino.rtp || {}
);
const selectPlatformSettingLimits = createSelector(
  getPlatformSettingsState,
  (s) => s.limits
);
const selectPlatformSettingsDisplay = createSelector(
  getPlatformSettingsState,
  (s) => s.display
);
const selectPlatformSettingLimitsTheme = createSelector(
  getPlatformSettingsState,
  (s) => s.limitsTheme
);
const selectPlatformSettingInactivityTime = createSelector(
  getPlatformSettingsState,
  (s) => s.inactivityLogoutPeriod
);
const selectActivePersonalCode = createSelector(
  getPlatformSettingsState,
  (s) => s.activePersonalCode
);
const selectPlatformSettingLimitsEnabled = createSelector(
  getPlatformSettingsState,
  (s) => !!Object.keys(s.limits).find((key) => s.limits[key] && key !== "popup")
);
const selectPlatformSettingLimitsPopupEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.limits.popup || false
);
const selectLimitsSelfExclusionEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.limits.selfExclusionEnabled
);
const selectLimitsFixedSelfExclusion = createSelector(
  getPlatformSettingsState,
  (s) => s.limits.fixedSelfExclusion
);
const selectLimitsSelfDeletionEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.limits.selfDeletionEnabled
);
const selectLimitsTimeoutEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.limits.timeoutLimitEnabled
);
const selectPlatformSettingsLoginTypes = createSelector(
  getPlatformSettingsState,
  (s) => s.login
);
const selectPlatformSettingsSportBook = createSelector(
  getPlatformSettingsState,
  (s) => s.sportsbook
);
const selectPlatformLiveSportContentEnabled = createSelector(
  selectPlatformSettingsSportBook,
  (s) => s == null ? void 0 : s.liveSportContentEnabled
);
const selectPlatformPrematchSportContentEnabled = createSelector(
  selectPlatformSettingsSportBook,
  (s) => s.prematchSportContentEnabled
);
const selectPlatformForecastVersion = createSelector(
  [selectPlatformSettingsSportBook],
  (sportBook) => sportBook == null ? void 0 : sportBook.forecastVersion
);
const selectPlatformSettingsSportBookTax = createSelector(
  [selectPlatformSettingsSportBook],
  (sportBook) => sportBook && "coupon" in sportBook ? sportBook.coupon.tax : null
);
const selectPlatformSettingsIsLogoEnabled = createSelector(
  [selectPlatformSettingsSportBook],
  (sportBook) => sportBook ? sportBook.competitorLogoEnabled : false
);
const selectPlatformSettingsTicketPrintEnabled = createSelector(
  [selectPlatformSettingsSportBook],
  (sportBook) => sportBook ? sportBook.ticketPrintEnabled : false
);
const selectIncomingBetsFeatureEnabled = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a, _b;
    return (_b = (_a = s.sportsbook) == null ? void 0 : _a.incomingBets) == null ? void 0 : _b.enabled;
  }
);
const selectIncomingBetsAutoUpdate = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a, _b;
    return (_b = (_a = s.sportsbook) == null ? void 0 : _a.incomingBets) == null ? void 0 : _b.autoUpdate;
  }
);
const selectPlatformSettingsLoaded = createSelector(
  getPlatformSettingsState,
  (s) => s.loaded
);
const generateLogoPath = createSelector(
  (logoUrl) => logoUrl,
  (logoUrl, competitorId) => competitorId,
  (logoUrl, competitorId) => `${logoUrl}/${competitorId}.png`
);
const selectPlatformSettingsError = createSelector(
  getPlatformSettingsState,
  (s) => s.error
);
const selectPgwCountries = createSelector(
  getPlatformSettingsState,
  (s) => s.pgw.widget_countries
);
const selectIsLayoutFrozen = createSelector(
  getPlatformSettingsState,
  (s) => s.layoutIsFrozen
);
const selectFooterMaxVisualPaymentCount = createSelector(
  getPlatformSettingsState,
  (s) => s.footer.paymentMethodsMaxVisualCount
);
const selectFooterBiggerHeightPaymentMethods = createSelector(
  getPlatformSettingsState,
  (s) => s.footer.biggerHeightPaymentMethods || []
);
const selectFooterFixedPaymentMethods = createSelector(
  getPlatformSettingsState,
  (s) => s.footer.fixedPaymentMethods || []
);
const selectFooterDefaultPaymentMethods = createSelector(
  getPlatformSettingsState,
  (s) => s.footer.defaultPaymentMethods || []
);
const selectFooterAgeIconsSet = createSelector(
  getPlatformSettingsState,
  (s) => Array.isArray(s.footer.ageIconsSet) ? s.footer.ageIconsSet : []
);
const selectFooterPaymentMethodsWithFrame = createSelector(
  getPlatformSettingsState,
  (s) => s.footer.footerPaymentMethodsWithFrame
);
const selectIsCustomerPaymentMethodsLoadBlocked = createSelector(
  getPlatformSettingsState,
  (s) => s.footer.blockCustomerPaymentMethodsLoad
);
const selectFooterPgwPaymentMethodsBlackList = createSelector(
  getPlatformSettingsState,
  (s) => {
    const blackList = s.footer.footerPgwPaymentMethodsBlackList;
    return Array.isArray(blackList) ? blackList : [];
  }
);
const selectFooterEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.footer.isFooterEnabled
);
const selectNewFooterEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.footer.newFooterEnabled
);
const selectAdditionalFooterLogo = createSelector(
  getPlatformSettingsState,
  (s) => s.footer.additionalFooterLogo
);
const selectShowLocalTimeTextWithoutSeconds = createSelector(
  getPlatformSettingsState,
  (s) => s.header.showLocalTimeTextWithoutSeconds
);
const selectResultsDisabled = createSelector(
  getPlatformSettingsState,
  (s) => s.resultsDisabled
);
const selectMobileAppVisibleElements = createSelector(
  getPlatformSettingsState,
  (s) => s.visibleMobileAppElements || []
);
const selectVipSportCustomRewards = createSelector(
  getPlatformSettingsState,
  (s) => s.vipSportCustomRewards
);
const selectVipSportProgramImages = createSelector(
  getPlatformSettingsState,
  (s) => s.vipSportProgramImages
);
const selectVipCasinoProgramImages = createSelector(
  getPlatformSettingsState,
  (s) => s.vipCasinoProgramImages
);
const selectSportSettings = createSelector(
  getPlatformSettingsState,
  (s) => s.sportSettings
);
const selectHideCompetitorsLogos = createSelector(
  selectSportSettings,
  (s) => s == null ? void 0 : s.hideCompetitorsLogos
);
const selectBffEnabled = createSelector(
  selectPlatformSettingsSportBook,
  (platformSettings) => platformSettings.bffEnabled || false
);
const selectSmartIdRegistration = createSelector(
  getPlatformSettingsState,
  (s) => s.isNewSmartIdRegistration
);
const selectShowOccupationField = createSelector(
  getPlatformSettingsState,
  (s) => s.showOccupationField
);
const selectShowSupportLinkOnCashier = createSelector(
  getPlatformSettingsState,
  (s) => s.showSupportLinkOnCashier
);
const selectSaveSelectedBonusInCashier = createSelector(
  getPlatformSettingsState,
  (s) => s.saveSelectedBonusInCashier
);
const selectBettingHistoryLimitedP3M = createSelector(
  getPlatformSettingsState,
  (s) => s.bettingHistoryLimitedP3M
);
const selectForecastHistoryLimitedP3M = createSelector(
  getPlatformSettingsState,
  (s) => s.forecastHistoryLimitedP3M
);
const selectRedesignNotificationModalEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.isRedesignNotificationModalEnabled
);
const selectIsModernSelfExclusion = createSelector(
  getPlatformSettingsState,
  (s) => s.isModernSelfExclusion
);
const selectShowZeroDepositLimitModal = createSelector(
  getPlatformSettingsState,
  (s) => s.showZeroDepositLimitModal
);
const selectRedesignRestorePasswordFormEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.isRedesignRestorePasswordFormEnabled
);
const selectIsLocalTimeShown = createSelector(
  getPlatformSettingsState,
  (s) => s.showLocalTime
);
const selectIsSessionTimeShown = createSelector(
  getPlatformSettingsState,
  (s) => s.showSessionTime
);
const selectIsLastLogInTimeShown = createSelector(
  getPlatformSettingsState,
  (s) => s.showLastLogInTime
);
const selectDefaultCurrency = createSelector(
  getPlatformSettingsState,
  (s) => s.defaultCurrency
);
const selectMenuLimitsAvailable = createSelector(
  getPlatformSettingsState,
  (s) => s.menuLimitsAvailable
);
const selectIsTemplatesPageVisible = createSelector(
  getPlatformSettingsState,
  (s) => s.templatesVisible
);
const selectSmartLocation = createSelector(
  getPlatformSettingsState,
  (s) => s.locationSmart
);
const selectSmartLocationTimeout = createSelector(
  getPlatformSettingsState,
  (s) => s.locationSmartTimeout
);
const selectIsBetSettingsDescriptionVisible = createSelector(
  getPlatformSettingsState,
  (s) => s.showBetSettingsDescription
);
const selectShowResponsibleGamingPopup = createSelector(
  getPlatformSettingsState,
  (s) => s.showResponsibleGamingPopup
);
const selectCustomLinkInHeaderMenu = createSelector(
  getPlatformSettingsState,
  (s) => s.customLinkInHeaderMenu
);
const selectAdditionalHeaderLogo = createSelector(
  getPlatformSettingsState,
  (s) => s.header.additionalHeaderLogo
);
const selectLogoDesktopUrl = createSelector(
  getPlatformSettingsState,
  (s) => s.logo.logoUrlDesktop
);
const selectLogoMobileUrl = createSelector(
  getPlatformSettingsState,
  (s) => {
    return s.logo.logoUrlMobile || s.logo.logoUrlDesktop;
  }
);
const selectAsideMenuLogoMobileUrl = createSelector(
  getPlatformSettingsState,
  (s) => {
    return s.logo.altLogoUrlMobile || s.logo.logoUrlMobile || s.logo.logoUrlDesktop;
  }
);
const selectBackgroundAppLoader = createSelector(
  getPlatformSettingsState,
  (s) => s.loaderBackgroundColor
);
const selectSiteMenuAdditionalItems = createSelector(
  getPlatformSettingsState,
  (s) => s.siteMenuAdditionalItems
);
const selectIsVisibleExtendedTransactionHistory = createSelector(
  getPlatformSettingsState,
  (s) => s.showExtendedTransactionHistory
);
const selectIsCasinoSessionHistoryActive = createSelector(
  getPlatformSettingsState,
  (s) => s.showCasinoSessionHistory
);
const selectComponentLoaderIcon = createSelector(
  getPlatformSettingsState,
  (s) => s.componentLoaderIcon
);
const selectComponentLoaderImage = createSelector(
  getPlatformSettingsState,
  (s) => s.componentLoaderImage
);
const selectNewCashier = createSelector(
  getPlatformSettingsState,
  (s) => s.newCashier
);
const selectPreselectedBonusType = createSelector(
  getPlatformSettingsState,
  (s) => s.preselectedBonusType
);
const selectCustomerSlideForMainPageBanner = createSelector(
  getPlatformSettingsState,
  (s) => s.customerSlideForMainPageBanner
);
const selectIsFitToPlayRequired = createSelector(
  getPlatformSettingsState,
  ({ authorisation }) => {
    const { isFitToPlayRequired } = authorisation || {};
    return isFitToPlayRequired;
  }
);
const selectCountryCertificationData = createSelector(
  getPlatformSettingsState,
  (s, countryCode) => (countryCode || "").toUpperCase(),
  (s, countryCode) => s.certification.countryLimitsSettings[countryCode] || {}
);
const selectCertificationLimitsRedirect = createSelector(
  getPlatformSettingsState,
  (s) => s.certification.limitsRedirect || {}
);
const selectCertificationLimitsDeposit = createSelector(
  getPlatformSettingsState,
  (s) => s.certification.limitsDeposit || {}
);
const selectCertificationShowBalanceOption = createSelector(
  getPlatformSettingsState,
  (s) => s.certification.displayHideBalanceOption || false
);
const selectIsHorseRacingEnabled = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a, _b;
    return !!((_b = (_a = s.sport) == null ? void 0 : _a.racing) == null ? void 0 : _b.racingEnabled);
  }
);
const selectIsHorseRacingViewType = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a, _b;
    return (_b = (_a = s.sport) == null ? void 0 : _a.racing) == null ? void 0 : _b.viewType;
  }
);
const selectLatrobetApiUrl = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a, _b;
    return (_b = (_a = s.sport) == null ? void 0 : _a.racing) == null ? void 0 : _b.latrobetSessionApiUrl;
  }
);
const selectLatrobetCompanyName = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a, _b;
    return (_b = (_a = s.sport) == null ? void 0 : _a.racing) == null ? void 0 : _b.companyName;
  }
);
const selectRacingAvailableCurrencies = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a, _b;
    return (_b = (_a = s.sport) == null ? void 0 : _a.racing) == null ? void 0 : _b.availableCurrencies;
  }
);
const selectBetBuilderEnabled = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a;
    return (_a = s.sport) == null ? void 0 : _a.betBuilderEnabled;
  }
);
const selectBetBuilderConfig = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a;
    return (_a = s.sport) == null ? void 0 : _a.betBuilder;
  }
);
const selectShowCasinoDemoGames = createSelector(
  getPlatformSettingsState,
  (s) => s.showCasinoDemoGames
);
const selectIsShowCasinoBg = createSelector(
  getPlatformSettingsState,
  (s) => s.isShowCasinoBg
);
const selectHorseRacingWidgetConfig = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a, _b;
    if ((_a = s.sport) == null ? void 0 : _a.racing) {
      const {
        env,
        client,
        platformUrl,
        viewType,
        betPlacementUrl,
        pusherKey
      } = ((_b = s.sport) == null ? void 0 : _b.racing) || {};
      return {
        env,
        client,
        platformUrl,
        viewType,
        betPlacementUrl,
        pusherKey
      };
    }
    return null;
  }
);
const selectNewMainPage = createSelector(
  getPlatformSettingsState,
  (s) => s.newMainPage
);
const selectNewMainPageEnabled = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a;
    return (_a = s.newMainPage) == null ? void 0 : _a.enabled;
  }
);
const selectNewMainPageComponents = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a;
    return (_a = s.newMainPage) == null ? void 0 : _a.components;
  }
);
const selectNewMainPageComponentByNameEnabled = createSelector(
  getPlatformSettingsState,
  (s, name) => name,
  (s, name) => {
    var _a, _b, _c;
    return (_c = (_b = (_a = s.newMainPage) == null ? void 0 : _a.components) == null ? void 0 : _b[name]) == null ? void 0 : _c.enabled;
  }
);
const selectNewMainPageComponentBySettings = createSelector(
  getPlatformSettingsState,
  (s, name) => name,
  (s, name) => {
    var _a, _b, _c;
    return ((_c = (_b = (_a = s.newMainPage) == null ? void 0 : _a.components) == null ? void 0 : _b[name]) == null ? void 0 : _c.settings) || {};
  }
);
const selectPromoOfferPopupEnabled = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a;
    return (_a = s.promoOfferPopup) == null ? void 0 : _a.enabled;
  }
);
const selectPromoOfferPopup = createSelector(
  getPlatformSettingsState,
  (s) => s.promoOfferPopup || {}
);
const selectCertificateInitialLimits = createSelector(
  getPlatformSettingsState,
  (s, countryCode) => (countryCode || "").toUpperCase(),
  (s, countryCode) => {
    var _a, _b;
    return {
      [
        "wagerPerPeriod"
        /* wagerPerPeriod */
      ]: ((_a = s.certification.countryLimitsSettings[countryCode]) == null ? void 0 : _a.limits.wagerPerPeriod) ? {
        amount: s.certification.countryLimitsSettings[countryCode].limits.wagerPerPeriod || null,
        periodType: PeriodType.days
      } : null,
      [
        "singleBet"
        /* singleBet */
      ]: ((_b = s.certification.countryLimitsSettings[countryCode]) == null ? void 0 : _b.limits.singleBet) ? {
        amount: s.certification.countryLimitsSettings[countryCode].limits.singleBet || null
      } : null
    };
  }
);
const selectCertificateLimitsRedirect = createSelector(
  getPlatformSettingsState,
  (s) => s.certification || {}
);
const selectReferralsLink = createSelector(
  getPlatformSettingsState,
  (s) => s.referralsLink
);
const selectHomePageBannerLinks = createSelector(
  getPlatformSettingsState,
  (s) => s.homePageBannerLinks
);
const selectComboBoostBannerLink = createSelector(
  getPlatformSettingsState,
  (s) => s.comboBoostBanner
);
const selectVipProgramBanners = createSelector(
  getPlatformSettingsState,
  (s) => s.vipProgramBanners || []
);
const selectAmbassadorsLink = createSelector(
  getPlatformSettingsState,
  (s) => s.ambassadorsLink
);
const selectHallOfFameEnabled = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a;
    return !!((_a = s.hallOfFame) == null ? void 0 : _a.enabled);
  }
);
const selectHallOfFameData = createSelector(
  getPlatformSettingsState,
  (s) => s.hallOfFame
);
const selectIntegrations = createSelector(
  getPlatformSettingsState,
  (s) => s.integrations
);
const selectQrCodeImageLinkAppDownload = createSelector(
  getPlatformSettingsState,
  (s) => s.qrCodeImageLinkAppDownload
);
const selectMobileAppDownloadLinkIos = createSelector(
  getPlatformSettingsState,
  (s) => s.mobileAppDownloadLinkIos
);
const selectAuthProviders = createSelector(
  getPlatformSettingsState,
  (s, country) => country,
  (s, country, isEparakstsEnabled) => isEparakstsEnabled,
  ({ authorisation }, country, isEparakstsEnabled) => {
    const { authProviders } = authorisation || {};
    const isAuthProviders = authProviders && Object.keys(authProviders).length > 0;
    const providersByCountry = isAuthProviders && authProviders[country] || [AuthProvidersList.EMAIL];
    return providersByCountry.filter((provider) => provider === AuthProvidersList.EPARAKSTS ? isEparakstsEnabled : true);
  }
);
const selectCombisEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.combisEnabled
);
const selectBoostedOddsEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.boostedOddsEnabled
);
const selectCashierDepositBonusUnselected = createSelector(
  getPlatformSettingsState,
  (s) => s.unselectCashierDepositBonus
);
const selectWheelOfFortuneEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.wheelOfFortuneEnabled
);
const selectAmbassadorsHomeBanner = createSelector(
  getPlatformSettingsState,
  (s) => s.ambassadorsHomeBanner
);
const selectRealTimeIncomingBetsEventAmount = createSelector(
  selectPlatformSettingsSportBook,
  (sportsBook) => {
    var _a;
    return (_a = sportsBook == null ? void 0 : sportsBook.realTimeIncomingBets) == null ? void 0 : _a.eventAmount;
  }
);
const selectFavoritesEnabled = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a;
    return !!((_a = s.sport) == null ? void 0 : _a.favoriteEnabled);
  }
);
const selectPinEnabled = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a;
    return !!((_a = s.sport) == null ? void 0 : _a.pinEnabled);
  }
);
const selectFavoriteEnabled = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a;
    return !!((_a = s.sport) == null ? void 0 : _a.favoriteEnabled);
  }
);
const selectImageForPromotionPage = createSelector(
  getPlatformSettingsState,
  (s) => s.imageForPromotionPage
);
const selectCategoryRecentlyPlayedEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.categoryRecentlyPlayedEnabled
);
const selectCategoryRecommendedEnabled = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a;
    return (_a = s.zingRecommendations) == null ? void 0 : _a.enabled;
  }
);
const selectRecommendedCategoriesListEnabled = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a;
    return (_a = s.zingRecommendations) == null ? void 0 : _a.categoriesList;
  }
);
const selectMainRecommendedNonAuthEnabled = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a, _b;
    return ((_a = s.zingRecommendations) == null ? void 0 : _a.enabled) && ((_b = s.zingRecommendations) == null ? void 0 : _b.nonAuthEnabled);
  }
);
const selectMainPageRecommendedEnabled = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a, _b;
    return ((_a = s.zingRecommendations) == null ? void 0 : _a.enabled) && ((_b = s.zingRecommendations) == null ? void 0 : _b.mainPageSection);
  }
);
const selectNewCasinoBannerEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.newCasinoBanner
);
const selectPhoneMarketingAllowedDisabled = createSelector(
  getPlatformSettingsState,
  (s) => s.phoneMarketingAllowedDisabled
);
const selectNewMarketingPreferencesEnabled = createSelector(
  getPlatformSettingsState,
  (s) => !!s.newMarketingPreferencesEnabled
);
const selectNewMarketingPreferences = createSelector(
  getPlatformSettingsState,
  (s) => s.newMarketingPreferences
);
const selectCasinoLiveBannerImages = createSelector(
  getPlatformSettingsState,
  (s) => s.casinoLiveBannerImages
);
const selectCasinoLineBannerImages = createSelector(
  getPlatformSettingsState,
  (s) => s.casinoLineBannerImages
);
const selectBonusesEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.bonusesEnabled
);
const selectisBlockedVip = createSelector(
  getPlatformSettingsState,
  (s) => s.isBlockedVip
);
const selectTaxStrategyType = createSelector(
  getPlatformSettingsState,
  ({ sportsbook }) => {
    const { coupon } = sportsbook || {};
    const { tax } = coupon || {};
    return (tax == null ? void 0 : tax.enabled) && (tax == null ? void 0 : tax.strategyType);
  }
);
const selectIsTaxDepositStrategy = createSelector(
  selectTaxStrategyType,
  (strategyType) => {
    return strategyType === TaxStrategyTypes.deposit;
  }
);
const selectBannersFromCms = createSelector(
  getPlatformSettingsState,
  (s) => s.bannersFromCms
);
const selectCustomButtonHeaderMenu = createSelector(
  getPlatformSettingsState,
  (s) => s.campeonatoCl
);
const selectAsiaSkin = createSelector(
  getPlatformSettingsState,
  (s) => s.asia
);
const selectAsiaSkinEnabled = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a;
    return (_a = s == null ? void 0 : s.asia) == null ? void 0 : _a.enabled;
  }
);
const selectAsiaSkinSettingByName = createSelector(
  selectAsiaSkin,
  (asia, name) => name,
  (asia, name) => (asia == null ? void 0 : asia.enabled) && (asia == null ? void 0 : asia[name])
);
const selectAsiaSkinSettingByNameEnabled = createSelector(
  selectAsiaSkin,
  (asia, name) => name,
  (asia, name) => {
    var _a;
    return (asia == null ? void 0 : asia.enabled) && ((_a = asia == null ? void 0 : asia[name]) == null ? void 0 : _a.enabled);
  }
);
const selectIsReferralProgramByPlatformEnabled = createSelector(
  getPlatformSettingsState,
  ({ refProgramEnabled }) => !!refProgramEnabled
);
const selectSocialNetworkAuthEnabled = createSelector(
  getPlatformSettingsState,
  ({ socialNetworkAuthEnabled }) => !!socialNetworkAuthEnabled
);
const selectIsUserRestrictionsAvailable = createSelector(
  getPlatformSettingsState,
  (s) => s.isUserRestrictionsAvailable
);
const selectShowNecctonNotificationEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.showNecctonNotificationEnabled
);
const selectShowTreshholdHitSnackEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.showTreshholdHitSnackEnabled
);
const selectTrackingGameEnabled = createSelector(
  getPlatformSettingsState,
  (s) => {
    var _a;
    return (_a = s.zingRecommendations) == null ? void 0 : _a.trackingGameEnabled;
  }
);
const selectApiRenderMenuByNameEnabled = createSelector(
  getPlatformSettingsState,
  (s, name) => name,
  (s, name) => {
    var _a, _b, _c;
    return (_c = (_b = (_a = s == null ? void 0 : s.apiRenderComponents) == null ? void 0 : _a[ApiRenderComponents.menu]) == null ? void 0 : _b[name]) == null ? void 0 : _c.enabled;
  }
);
const selectCasinoAggregatedCategoryEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s == null ? void 0 : s.casinoAggregatedCategoryEnabled
);
const selectNewTopMenu = createSelector(
  getPlatformSettingsState,
  (s) => s.newTopMenu
);
const selectMobileCustomerBanner = createSelector(
  getPlatformSettingsState,
  (s) => s.mobileCustomBanner
);
const selectMobileCustomBannerLastWin = createSelector(
  getPlatformSettingsState,
  (s) => s.mobileCustomBannerLastWin
);
const selectCustomRegistrationBannerImages = createSelector(
  getPlatformSettingsState,
  (s) => s.customRegistrationBannerImages
);
const selectemailHintsEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.emailHintsEnabled
);
const selectEmailHints = createSelector(
  getPlatformSettingsState,
  (s) => s.emailHints
);
const selectMobileBottomMenu = createSelector(
  getPlatformSettingsState,
  (s) => s == null ? void 0 : s.mobileBottomMenu
);
const selectVrButtonInMenuEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s == null ? void 0 : s.vrButtonInMenuEnabled
);
const selectCasinoVipLevelsCount = createSelector(
  getPlatformSettingsState,
  (s) => s == null ? void 0 : s.casinoVipLevelsCount
);
const selectVerificationBlockFromCms = createSelector(
  getPlatformSettingsState,
  (s) => s.verificationBlockFromCms
);
const selectSelfExclusionBlockFromCms = createSelector(
  getPlatformSettingsState,
  (s) => s.selfExclusionBlockFromCms
);
const selectNewBannersFromCms = createSelector(
  getPlatformSettingsState,
  (s) => s.newBannersFromCms
);
const selectCasinoMobileBottomMenu = createSelector(
  getPlatformSettingsState,
  (s) => s == null ? void 0 : s.casinoMobBotMenu
);
const selectCasinoDemoRealSwitch = createSelector(
  getPlatformSettingsState,
  (s) => s == null ? void 0 : s.casinoDemoRealSwitch
);
const selectSlotUi = createSelector(
  getPlatformSettingsState,
  (s) => s == null ? void 0 : s.slotUi
);
const selectNewOndatoVerificationPageEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.newOndatoVerificationPageEnabled
);
const selectBonusHub = createSelector(
  getPlatformSettingsState,
  (s) => s.bonusHub
);
const selectRandomSlotEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.showRandomSlot
);
const selectBalanceResidualLimitVersion = createSelector(
  getPlatformSettingsState,
  (s) => s.limits.balanceResidualLimitVersion
);
const selectIsRequiredFieldsFromCashier = createSelector(
  getPlatformSettingsState,
  ({ requiredFieldsFromCashier }) => requiredFieldsFromCashier
);
const getConfigurablePersonalDataSetup = createSelector(
  getPlatformSettingsState,
  ({ configurablePersonalDetails }) => configurablePersonalDetails
);
const selectIsConfigurableCashierEnabled = createSelector(
  getPlatformSettingsState,
  ({ isConfigurableCashierEnabled }) => isConfigurableCashierEnabled
);
const selectIsCheckCashierNotification = createSelector(
  getPlatformSettingsState,
  ({ checkCashierNotification }) => checkCashierNotification
);
const selectProducerIconUrl = createSelector(
  getPlatformSettingsState,
  (s) => s
);
const selectBluemRgSmartAssistantEnabled = createSelector(
  getPlatformSettingsState,
  ({ bluemRgSmartAssistantEnabled }) => bluemRgSmartAssistantEnabled
);
const selectIFinanceCheckEnabled = createSelector(
  getPlatformSettingsState,
  ({ iFinanceCheckEnabled }) => iFinanceCheckEnabled
);
const selectShowVideoBanner = createSelector(
  getPlatformSettingsState,
  (s) => s.showVideoBanner
);
const selectShowWheelOnStart = createSelector(
  getPlatformSettingsState,
  (s) => s.showWheelOnStart
);
const selectWheelOfFortuneSkin = createSelector(
  getPlatformSettingsState,
  (s) => s == null ? void 0 : s.wheelOfFortuneSkin
);
const selectDefaultWheelNonAuth = createSelector(
  getPlatformSettingsState,
  (s) => s == null ? void 0 : s.defaultWheelNonAuth
);
const selectImageCfg = createSelector(
  getPlatformSettingsState,
  (s) => s.gameImageCfg
);
const selectIsMaterialStyle = createSelector(
  getPlatformSettingsState,
  (s) => !!(s == null ? void 0 : s.isMaterial)
);
const selectCustomInputValidation = createSelector(
  getPlatformSettingsState,
  (s) => (s == null ? void 0 : s.customInputValidation) || {}
);
const selectDisabledShowModals = createSelector(
  getPlatformSettingsState,
  (s) => s == null ? void 0 : s.disabledShowModals
);
const selectInputHasCustomValidation = createSelector(
  [selectCustomInputValidation, (s, fieldName) => fieldName],
  ({ enabled, allowedValidation }, fieldName) => enabled && allowedValidation.includes(fieldName)
);
const selectFloatingLabel = createSelector(
  getPlatformSettingsState,
  (s) => !!(s == null ? void 0 : s.floatingLabel)
);
const selectIsMaterialRegistration = createSelector(
  getPlatformSettingsState,
  (s) => !!(s == null ? void 0 : s.isMaterialRegistration)
);
const selectBluemRgSmartAssistantSdkApiUrl = createSelector(
  getPlatformSettingsState,
  (s) => s.bluemRgSmartAssistantSdkApiUrl
);
const selectIsModalClosableOnSwipe = createSelector(
  getPlatformSettingsState,
  (s) => s.isModalClosableOnSwipe
);
const selectIsSmarticoEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.smarticoEnabled
);
const selectIsGamixterEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.gamixterEnabled
);
const selectDOBPlatformFormat = createSelector(
  getPlatformSettingsState,
  (s) => s == null ? void 0 : s.dobFormat
);
const selectShowCasinoDropdownsInAsideMenu = createSelector(
  getPlatformSettingsState,
  (s) => s.showCasinoDropdownsInAsideMenu
);
const selectCheckCashierConfig = createSelector(
  getPlatformSettingsState,
  (s) => s == null ? void 0 : s.checkCashierConfig
);
const selectLootBoxEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.gamixterSettings.lootBoxEnabled
);
const selectCashbackWidgetSettings = createSelector(
  getPlatformSettingsState,
  (s) => s.cashbackWidgetSettings
);
const selectSectionsConfig = createSelector(
  getPlatformSettingsState,
  (s) => s.pageSectionsConfig || []
);
const selectSectionsFromSectionsConfigByPage = createSelector(
  selectSectionsConfig,
  (config, page) => page,
  (config, page) => {
    var _a;
    return ((_a = config.find((config2) => config2.page === page)) == null ? void 0 : _a.sections) || [];
  }
);
const selectShowLastWinners = createSelector(
  getPlatformSettingsState,
  (s) => s.showLastWinners
);
const selectPwaInstallButtonSettings = createSelector(
  getPlatformSettingsState,
  (s) => s.pwaInstallButtonSettings
);
const selectDisableDemoGameForProviders = createSelector(
  getPlatformSettingsState,
  (s) => s.disableDemoGameForProviders
);
const selectRegistrationBaseAutoScroll = createSelector(
  getPlatformSettingsState,
  (s) => s.registrationBaseAutoScroll
);
const selectUseNewDobAutofocus = createSelector(
  getPlatformSettingsState,
  (s) => s.useNewDobAutofocus
);
const selectOndatoVerificationPopupInCasier = createSelector(
  getPlatformSettingsState,
  (s) => s.ondatoVerificationPopupInCasier
);
const selectPlatformSettingsIsText = createSelector(
  getPlatformSettingsState,
  (s) => s.isTextPage
);
const selectIsCasinoMaxBetPopupEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.casinoMaxBetPopup
);
const selectShowRegFormOnStart = createSelector(
  getPlatformSettingsState,
  (s) => s.showRegFormOnStart
);
const selectPersonalPreferences = createSelector(
  getPlatformSettingsState,
  (s) => s.personalPreferencesSettings
);
const selectСasinoPageRedesigned = createSelector(
  getPlatformSettingsState,
  (s) => s.casinoPageRedesigned
);
const selectRestyledPagesEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.restyledPagesEnabled
);
const selectGamePreloaderImages = createSelector(
  getPlatformSettingsState,
  (s) => s.gamePreloaderImages
);
const selectRegistrationSuccessLogoUrl = createSelector(
  getPlatformSettingsState,
  (s) => s.registrationSuccessLogoUrl
);
const selectIsEnableWelcomeLogin = createSelector(
  getPlatformSettingsState,
  (s) => s.enableWelcomeLogin
);
const selectRestrictionConfirmationPopup = createSelector(
  getPlatformSettingsState,
  (s) => s.restrictionConfirmationPopup
);
const selectFastDeposit = createSelector(
  getPlatformSettingsState,
  (s) => s == null ? void 0 : s.fastDeposit
);
const selectFullPlatformSettingsLoaded = createSelector(
  getPlatformSettingsState,
  (s) => s.fullSettingsLoaded
);
const selectPrizeHubEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.prizeHubEnabled
);
const selectAnimatedSignUpButton = createSelector(
  getPlatformSettingsState,
  (s) => s.animatedSignUpButton
);
const selectSlotsgemRegLoginAdjustments = createSelector(
  getPlatformSettingsState,
  (s) => s.slotsgemRegLoginAdjustments
);
const selectCurrencyConversionPopupEnabled = createSelector(
  getPlatformSettingsState,
  (s) => s.currencyConversionPopupEnabled
);
export {
  LimitNames,
  generateLogoPath,
  getConfigurablePersonalDataSetup,
  selectActivePersonalCode,
  selectAdditionalFooterLogo,
  selectAdditionalHeaderLogo,
  selectAmbassadorsHomeBanner,
  selectAmbassadorsLink,
  selectAnimatedSignUpButton,
  selectApiRenderMenuByNameEnabled,
  selectAsiaSkin,
  selectAsiaSkinEnabled,
  selectAsiaSkinSettingByName,
  selectAsiaSkinSettingByNameEnabled,
  selectAsideMenuLogoMobileUrl,
  selectAuthProviders,
  selectBackgroundAppLoader,
  selectBalanceResidualLimitVersion,
  selectBannersFromCms,
  selectBetBuilderConfig,
  selectBetBuilderEnabled,
  selectBetSharingEnabled,
  selectBettingHistoryLimitedP3M,
  selectBffEnabled,
  selectBluemRgSmartAssistantEnabled,
  selectBluemRgSmartAssistantSdkApiUrl,
  selectBonusHub,
  selectBonusesEnabled,
  selectBoostedOddsEnabled,
  selectCashbackWidgetSettings,
  selectCashierDepositBonusUnselected,
  selectCasinoAggregatedCategoryEnabled,
  selectCasinoDemoRealSwitch,
  selectCasinoLineBannerImages,
  selectCasinoLiveBannerImages,
  selectCasinoMobileBottomMenu,
  selectCasinoVipLevelsCount,
  selectCategoryRecentlyPlayedEnabled,
  selectCategoryRecommendedEnabled,
  selectCertificateInitialLimits,
  selectCertificateLimitsRedirect,
  selectCertificationLimitsDeposit,
  selectCertificationLimitsRedirect,
  selectCertificationShowBalanceOption,
  selectCheckCashierConfig,
  selectCombisEnabled,
  selectComboBoostBannerLink,
  selectComponentLoaderIcon,
  selectComponentLoaderImage,
  selectCountryCertificationData,
  selectCurrencyConversionPopupEnabled,
  selectCustomButtonHeaderMenu,
  selectCustomInputValidation,
  selectCustomLinkInHeaderMenu,
  selectCustomRegistrationBannerImages,
  selectCustomerSlideForMainPageBanner,
  selectDOBPlatformFormat,
  selectDefaultCurrency,
  selectDefaultWheelNonAuth,
  selectDisableDemoGameForProviders,
  selectDisabledShowModals,
  selectEmailHints,
  selectFastDeposit,
  selectFavoriteEnabled,
  selectFavoritesEnabled,
  selectFloatingLabel,
  selectFooterAgeIconsSet,
  selectFooterBiggerHeightPaymentMethods,
  selectFooterDefaultPaymentMethods,
  selectFooterEnabled,
  selectFooterFixedPaymentMethods,
  selectFooterMaxVisualPaymentCount,
  selectFooterPaymentMethodsWithFrame,
  selectFooterPgwPaymentMethodsBlackList,
  selectForecastHistoryLimitedP3M,
  selectFullPlatformSettingsLoaded,
  selectGamePreloaderImages,
  selectGrpcIsActiveByConfig,
  selectHallOfFameData,
  selectHallOfFameEnabled,
  selectHideCompetitorsLogos,
  selectHomePageBannerLinks,
  selectHorseRacingWidgetConfig,
  selectIFinanceCheckEnabled,
  selectImageCfg,
  selectImageForPromotionPage,
  selectIncomingBetsAutoUpdate,
  selectIncomingBetsFeatureEnabled,
  selectInputHasCustomValidation,
  selectIntegrations,
  selectIsBetSettingsDescriptionVisible,
  selectIsCasinoMaxBetPopupEnabled,
  selectIsCasinoSessionHistoryActive,
  selectIsCheckCashierNotification,
  selectIsConfigurableCashierEnabled,
  selectIsCustomerPaymentMethodsLoadBlocked,
  selectIsEnableWelcomeLogin,
  selectIsFitToPlayRequired,
  selectIsGamixterEnabled,
  selectIsHorseRacingEnabled,
  selectIsHorseRacingViewType,
  selectIsLastLogInTimeShown,
  selectIsLayoutFrozen,
  selectIsLocalTimeShown,
  selectIsMaterialRegistration,
  selectIsMaterialStyle,
  selectIsModalClosableOnSwipe,
  selectIsModernSelfExclusion,
  selectIsReferralProgramByPlatformEnabled,
  selectIsRequiredFieldsFromCashier,
  selectIsSessionTimeShown,
  selectIsShowCasinoBg,
  selectIsSmarticoEnabled,
  selectIsTaxDepositStrategy,
  selectIsTemplatesPageVisible,
  selectIsTermsAcceptRequired,
  selectIsUserRestrictionsAvailable,
  selectIsVisibleExtendedTransactionHistory,
  selectLatrobetApiUrl,
  selectLatrobetCompanyName,
  selectLeaguesWithSportIcons,
  selectLimitsFixedSelfExclusion,
  selectLimitsSelfDeletionEnabled,
  selectLimitsSelfExclusionEnabled,
  selectLimitsTimeoutEnabled,
  selectLiveEventsDisabled,
  selectLogoDesktopUrl,
  selectLogoMobileUrl,
  selectLootBoxEnabled,
  selectMainPageRecommendedEnabled,
  selectMainRecommendedNonAuthEnabled,
  selectMenuLimitsAvailable,
  selectMobileAppDownloadLinkIos,
  selectMobileAppVisibleElements,
  selectMobileBottomMenu,
  selectMobileCustomBannerLastWin,
  selectMobileCustomerBanner,
  selectNewBannersFromCms,
  selectNewCashier,
  selectNewCasinoBannerEnabled,
  selectNewFooterEnabled,
  selectNewMainPage,
  selectNewMainPageComponentByNameEnabled,
  selectNewMainPageComponentBySettings,
  selectNewMainPageComponents,
  selectNewMainPageEnabled,
  selectNewMarketingPreferences,
  selectNewMarketingPreferencesEnabled,
  selectNewOndatoVerificationPageEnabled,
  selectNewTopMenu,
  selectOndatoVerificationPopupInCasier,
  selectPersonalPreferences,
  selectPgwCountries,
  selectPhoneMarketingAllowedDisabled,
  selectPinEnabled,
  selectPlatformForecastVersion,
  selectPlatformLiveSportContentEnabled,
  selectPlatformPrematchSportContentEnabled,
  selectPlatformSettingInactivityTime,
  selectPlatformSettingLimits,
  selectPlatformSettingLimitsEnabled,
  selectPlatformSettingLimitsPopupEnabled,
  selectPlatformSettingLimitsTheme,
  selectPlatformSettingsCasinoEnabled,
  selectPlatformSettingsCasinoPlayersOnline,
  selectPlatformSettingsCasinoShowTopWinners,
  selectPlatformSettingsDisableSportsBetting,
  selectPlatformSettingsDisplay,
  selectPlatformSettingsError,
  selectPlatformSettingsIsFrontRenderText,
  selectPlatformSettingsIsLogoEnabled,
  selectPlatformSettingsIsText,
  selectPlatformSettingsLimitsDisablable,
  selectPlatformSettingsLiveCasinoEnabled,
  selectPlatformSettingsLoaded,
  selectPlatformSettingsLoginTypes,
  selectPlatformSettingsRtp,
  selectPlatformSettingsSmarticoActions,
  selectPlatformSettingsSportBook,
  selectPlatformSettingsSportBookTax,
  selectPlatformSettingsTicketPrintEnabled,
  selectPreselectedBonusType,
  selectPrizeHubEnabled,
  selectProducerIconUrl,
  selectPromoOfferPopup,
  selectPromoOfferPopupEnabled,
  selectPwaInstallButtonSettings,
  selectQrCodeImageLinkAppDownload,
  selectRacingAvailableCurrencies,
  selectRandomSlotEnabled,
  selectRealTimeIncomingBetsEventAmount,
  selectRecommendedCategoriesListEnabled,
  selectRedesignNotificationModalEnabled,
  selectRedesignRestorePasswordFormEnabled,
  selectReferralsLink,
  selectRegistrationBaseAutoScroll,
  selectRegistrationSuccessLogoUrl,
  selectRestrictionConfirmationPopup,
  selectRestyledPagesEnabled,
  selectResultsDisabled,
  selectSaveSelectedBonusInCashier,
  selectSectionsConfig,
  selectSectionsFromSectionsConfigByPage,
  selectSelfExclusionBlockFromCms,
  selectShowCasinoDemoGames,
  selectShowCasinoDropdownsInAsideMenu,
  selectShowLastWinners,
  selectShowLocalTimeTextWithoutSeconds,
  selectShowNecctonNotificationEnabled,
  selectShowOccupationField,
  selectShowRegFormOnStart,
  selectShowResponsibleGamingPopup,
  selectShowSupportLinkOnCashier,
  selectShowTreshholdHitSnackEnabled,
  selectShowVideoBanner,
  selectShowWheelOnStart,
  selectShowZeroDepositLimitModal,
  selectSiteMenuAdditionalItems,
  selectSlotUi,
  selectSlotsgemRegLoginAdjustments,
  selectSmartIdRegistration,
  selectSmartLocation,
  selectSmartLocationTimeout,
  selectSocialNetworkAuthEnabled,
  selectSportSettings,
  selectTaxStrategyType,
  selectTrackingGameEnabled,
  selectUseNewDobAutofocus,
  selectVerificationBlockFromCms,
  selectVipCasinoProgramImages,
  selectVipProgramBanners,
  selectVipSportCustomRewards,
  selectVipSportProgramImages,
  selectVrButtonInMenuEnabled,
  selectWheelOfFortuneEnabled,
  selectWheelOfFortuneSkin,
  selectWsIsActive,
  selectemailHintsEnabled,
  selectisBlockedVip,
  selectСasinoPageRedesigned,
  showLeagueOnlySportIcon
};
